import { currencySymbol } from '@/lib/calendar-utils'

/**
 * Display formatting for dashboards, insight cards and reports.
 *
 * These helpers add en-GB thousands separators ONLY. The backend is the source
 * of truth for every monetary value (see CLAUDE.md — "Data Integrity"), so they
 * MUST NOT round, floor, truncate or otherwise alter the number: `40896.69`
 * renders as `40,896.69`, never `40,897`.
 *
 * Display convention (identical to `formatRate` in `lib/calendar-utils`):
 * integers render as integers (`£1,250`), non-integers render with 2dp
 * (`£1,250.75`). The separator is inserted into the exact string the legacy
 * formatter produced, so the digits can never drift.
 *
 * Calendar surfaces deliberately keep using `formatRate`/`formatRateSym` —
 * do not swap them over.
 */

/** Insert `,` every three digits in the integer part of an already-formatted number string. */
function groupDigits(numeric: string): string {
  const negative = numeric.startsWith('-')
  const body = negative ? numeric.slice(1) : numeric
  const [integerPart, fractionPart] = body.split('.')
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${negative ? '-' : ''}${grouped}${fractionPart ? `.${fractionPart}` : ''}`
}

/** The digits of a money value, before the currency symbol is attached. */
function moneyDigits(amount: number): string {
  if (!Number.isFinite(amount)) return String(amount)
  return groupDigits(Number.isInteger(amount) ? String(amount) : amount.toFixed(2))
}

/**
 * Money with a currency code — `formatMoney(40896.69, 'GBP')` → `£40,896.69`.
 * Never rounds; integers stay integers.
 */
export function formatMoney(amount: number, currency: string): string {
  return `${currencySymbol(currency)}${moneyDigits(amount)}`
}

/**
 * Money with a pre-resolved symbol — `formatMoneySym(1250, '£')` → `£1,250`.
 * Never rounds; integers stay integers.
 */
export function formatMoneySym(amount: number, sym: string): string {
  return `${sym}${moneyDigits(amount)}`
}

/**
 * Non-money counts — `formatCount(3809)` → `3,809`.
 *
 * For quantities only (reservations, nights, room-nights, rate pushes). Never
 * use for years, IDs, lead-time day counts or percentages — those read wrong
 * with a separator (`2026`, not `2,026`).
 */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return String(n)
  return groupDigits(String(n))
}
